import React, { FC, Fragment, memo, useEffect, useRef, useState } from 'react';
import { Button, Popover, useTheme } from '@mui/material';
import orderBy from 'lodash/orderBy';
import take from 'lodash/take';
import moment from 'moment-timezone';
import { nanoid } from 'nanoid';

import { useTranslate } from 'src/shared/providers';
import { getFirstDayOfMonthByDate } from 'src/shared/utils';

import { MonthlyEventType } from './MonthlyCalendar';
import { MonthlyEvent } from './MonthlyEvent';
import './MonthlyEventList.styles.scss';

interface Props {
  day: moment.Moment;
  events: MonthlyEventType[];
  isLastCellInGrid: boolean;
}

export const MonthlyEventList: FC<Props> = memo(({ day, events, isLastCellInGrid }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const ref = useRef<HTMLDivElement | null>(null);
  const [hasOverflowingChildren, setHasOverflowingChildren] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const showMore = Boolean(anchorEl);
  const seeMoreButtonId = showMore ? nanoid() : undefined;

  const reducedEvents = hasOverflowingChildren ? take(events, 3) : events;

  useEffect(() => {
    if (!ref?.current || !ref.current.parentElement?.offsetHeight) {
      return;
    }

    setHasOverflowingChildren(ref.current.offsetHeight + 45 > ref.current.parentElement.offsetHeight);
  }, [events]);

  return (
    <div ref={ref} className={`monthly-calendar-events w-100 mt-2 ${theme.palette.mode}`}>
      {reducedEvents.map((event) => (
        <Fragment key={nanoid()}>
          <MonthlyEvent day={day} event={event} isLastCellInGrid={isLastCellInGrid} />
        </Fragment>
      ))}
      {hasOverflowingChildren && (
        <>
          <Button
            className="see-more-events-btn py-0 px-2"
            aria-describedby={seeMoreButtonId}
            variant="text"
            size="small"
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            {t('global.seeMore')}
          </Button>

          <Popover
            classes={{ paper: 'monthly-calendar-events p-3 mt-1 rounded' }}
            id={seeMoreButtonId}
            open={showMore}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            transformOrigin={{ vertical: 'center', horizontal: 'center' }}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <div>
              <div className="text-center mb-2">
                <p className="mb-0 text-uppercase d-block day-label">{day.format('ddd')}</p>
                <span className="day-number">
                  {getFirstDayOfMonthByDate(day).isSame(day, 'day') && (
                    <span className="me-1">{day.format('MMM')}</span>
                  )}
                  {day.format('DD')}
                </span>
              </div>
              {orderBy(events, [(event) => !event.popupOrder, 'popupOrder'], ['desc', 'asc']).map((event, index) => (
                <MonthlyEvent
                  key={`monthly-event-${event.range.startDate}-${index}`}
                  day={day}
                  event={event}
                  isLastCellInGrid={isLastCellInGrid}
                  showFullInfo
                />
              ))}
            </div>
          </Popover>
        </>
      )}
    </div>
  );
});
